// Iter121.3 — Shared watchlist picker + manage modal + hook.
//
// Extracted from MarketForge.js so Dashboard, StrategyScannerV2, and
// MarketScannerBridgeTile all use the SAME active-list source of truth.
// A user's active watchlist now controls every surface consistently.
//
// Exports:
//   - useActiveWatchlist()   — hook returning { watchlists, activeId,
//                              activeWatchlist, switchActive, refetch,
//                              loading, error }
//   - <WatchlistPicker>       — compact dropdown + manage gear
//   - <ManageWatchlistsModal> — inline CRUD dialog
//
// Every mutation goes through the hook so callers always stay in sync.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Settings as SettingsIcon, Loader2, X, Trash2, Upload, FileText, Download } from "lucide-react";
import { API, useAuth } from "@/App";


// Iter121.4 — CSV parser for the watchlist import UI. Takes a raw CSV
// text (small file, client-side parse) and returns an array of ticker
// symbols. Strategy:
//   * Split by newline. Ignore empty lines.
//   * Detect header row: if the first row's first cell looks like a
//     label ("symbol", "ticker", "name" — any casing) → drop it.
//   * Extract the FIRST cell of each remaining row. Trim whitespace,
//     strip wrapping quotes, uppercase.
//   * Cap client-side at 1000 to avoid pathological uploads (the
//     backend caps at 500 anyway).
//
// Not exported — the modal is the only caller.
function parseCsvSymbols(text) {
  if (!text || typeof text !== "string") return [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return [];
  const firstCell = (line) => {
    // Split on comma OR tab OR semicolon (Excel exports vary).
    const parts = line.split(/[,;\t]/);
    let cell = (parts[0] || "").trim();
    // Strip a surrounding quote pair.
    if (cell.startsWith('"') && cell.endsWith('"')) cell = cell.slice(1, -1);
    return cell.trim();
  };
  const isHeader = (cell) => /^(symbol|ticker|name|instrument)$/i.test(cell);
  const start = isHeader(firstCell(lines[0])) ? 1 : 0;
  const out = [];
  for (let i = start; i < lines.length && out.length < 1000; i += 1) {
    const cell = firstCell(lines[i]).toUpperCase();
    if (cell) out.push(cell);
  }
  return out;
}


// ───────────────────────────────────────────────────────────────────
// Hook — single source of truth for watchlists + active pointer.
// ───────────────────────────────────────────────────────────────────
export function useActiveWatchlist() {
  const [watchlists, setWatchlists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async (preferredActiveId) => {
    setLoading(true);
    setError(null);
    try {
      const [listsRes, activeRes] = await Promise.all([
        axios.get(`${API}/watchlists`),
        axios.get(`${API}/user/active-watchlist`),
      ]);
      const lists = Array.isArray(listsRes.data) ? listsRes.data : [];
      setWatchlists(lists);
      const resolvedActive =
        preferredActiveId
        || activeRes.data?.id
        || (lists.find((l) => l.is_active)?.id)
        || (lists[0]?.id)
        || null;
      setActiveId(resolvedActive);
      return { watchlists: lists, activeId: resolvedActive };
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load watchlists");
      return { watchlists: [], activeId: null };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const switchActive = useCallback(async (newId) => {
    if (!newId || newId === activeId) return;
    try {
      await axios.put(`${API}/user/active-watchlist`, { watchlist_id: newId });
      await refetch(newId);
      // Iter121.20 — broadcast the switch so the App-level LiveStream
      // provider (and any other subscribers) update in-lockstep. We
      // dispatch AFTER the server + local refetch settle so listeners
      // see the same state we do.
      try {
        window.dispatchEvent(new CustomEvent("arxmentis:active-watchlist-changed", {
          detail: { activeId: newId },
        }));
      } catch { /* older browsers — silent */ }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to switch watchlist");
    }
  }, [activeId, refetch]);

  const activeWatchlist = useMemo(
    () => (watchlists || []).find((w) => w.id === activeId) || null,
    [watchlists, activeId],
  );

  return { watchlists, activeId, activeWatchlist, switchActive, refetch, loading, error };
}


// ───────────────────────────────────────────────────────────────────
// Compact picker — dropdown with SHARED/PERSONAL groups + gear.
// `variant` toggles the visual footprint: "compact" for tight rails
// (MarketForge, MarketScannerBridgeTile); "expanded" for more roomy
// contexts (Dashboard, StrategyScannerV2 header).
// ───────────────────────────────────────────────────────────────────
export function WatchlistPicker({
  watchlists,
  activeId,
  onSwitch,
  onManage,
  variant = "compact",
  testidPrefix = "wl-picker",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const active = (watchlists || []).find((w) => w.id === activeId) || (watchlists || [])[0] || null;
  const shared = (watchlists || []).filter((w) => w.scope === "shared");
  const personal = (watchlists || []).filter((w) => w.scope === "personal");

  const isExpanded = variant === "expanded";
  const btnBase = isExpanded
    ? "text-[11px] px-3 py-1.5"
    : "text-[10px] px-2 py-1.5";

  const renderRow = (w) => (
    <button
      key={w.id}
      type="button"
      onClick={() => { onSwitch(w.id); setOpen(false); }}
      data-testid={`${testidPrefix}-item-${w.id}`}
      className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.1em] text-left ${
        w.id === activeId
          ? "bg-[#00FF88]/10 text-[#00FF88]"
          : "text-white hover:bg-[#1A1A20]"
      }`}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${w.scope === "shared" ? "bg-[#22D3FF]" : "bg-[#FFB800]"}`} />
        <span className="truncate">{w.name}</span>
        {w.is_default && (<span className="text-[#5F5F66] text-[8px]">·DEF</span>)}
      </span>
      <span className="text-[#5F5F66] text-[9px] flex-shrink-0">{w.item_count ?? 0}</span>
    </button>
  );

  return (
    <div className={`relative ${isExpanded ? "" : "mb-2"}`} ref={rootRef} data-testid={testidPrefix}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testidPrefix}-toggle`}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`flex-1 min-w-0 flex items-center justify-between gap-1.5 font-bold uppercase tracking-[0.12em] rounded-sm border ${btnBase} ${
            open
              ? "border-[#00FF88]/60 bg-[#00FF88]/10 text-[#00FF88]"
              : "border-[#26262B] text-white hover:border-[#00FF88]/40"
          }`}
        >
          <span className="flex items-center gap-1.5 min-w-0">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${active?.scope === "shared" ? "bg-[#22D3FF]" : "bg-[#FFB800]"}`} />
            <span className="truncate" data-testid={`${testidPrefix}-active-name`}>{active?.name || "Watchlist"}</span>
            {active?.item_count != null && (
              <span className="text-[#5F5F66] text-[9px] font-mono flex-shrink-0">({active.item_count})</span>
            )}
          </span>
          <ChevronDown size={11} className={`flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button
          type="button"
          onClick={onManage}
          data-testid={`${testidPrefix}-manage`}
          title="Manage watchlists"
          className="flex-shrink-0 p-1.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#3A3A40]"
        >
          <SettingsIcon size={11} />
        </button>
      </div>
      {open && (
        <div
          role="listbox"
          data-testid={`${testidPrefix}-menu`}
          className="absolute left-0 right-0 top-full mt-1 z-30 bg-[#0A0A0D] border border-[#26262B] rounded-sm shadow-2xl max-h-[300px] overflow-y-auto"
        >
          {shared.length > 0 && (
            <div className="text-[8px] uppercase tracking-[0.18em] px-2.5 py-1 text-[#5F5F66] border-b border-[#1A1A20] bg-[#08080A]">SHARED</div>
          )}
          {shared.map(renderRow)}
          {personal.length > 0 && (
            <div className="text-[8px] uppercase tracking-[0.18em] px-2.5 py-1 text-[#5F5F66] border-b border-[#1A1A20] bg-[#08080A]">PERSONAL</div>
          )}
          {personal.map(renderRow)}
          <button
            type="button"
            onClick={() => { setOpen(false); onManage(); }}
            data-testid={`${testidPrefix}-newlist-shortcut`}
            className="w-full flex items-center gap-1.5 px-2.5 py-1.5 border-t border-[#1A1A20] text-[10px] font-bold uppercase tracking-[0.1em] text-[#22D3FF] hover:bg-[#22D3FF]/10"
          >
            <span className="text-base leading-none">+</span> New list
          </button>
        </div>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// Manage modal — create / rename / delete lists. Default lists are
// renameable but not deletable (backend enforces).
// ───────────────────────────────────────────────────────────────────
export function ManageWatchlistsModal({
  watchlists,
  activeId,
  onClose,
  onRefresh,
  onSwitchActive,
  testidPrefix = "wl-manage",
}) {  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("personal");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  // Iter121.4 — CSV bulk-import state. `csvSymbols` is populated as
  // soon as the operator picks a file (we parse client-side so they
  // can see the count before submitting). `csvFilename` echoes back
  // for confidence. `importSummary` shows the last per-list import
  // outcome as a compact chip line.
  const [csvSymbols, setCsvSymbols] = useState([]);
  const [csvFilename, setCsvFilename] = useState("");
  const [importingId, setImportingId] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const csvInputRef = useRef(null);
  const rowCsvInputRef = useRef(null);
  const [rowImportTarget, setRowImportTarget] = useState(null); // list id currently receiving a per-row CSV

  const handleCsvPick = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const syms = parseCsvSymbols(text);
      setCsvSymbols(syms);
      setCsvFilename(file.name);
      setCreateError(null);
    } catch (_e) {
      setCreateError("Could not read the file");
    }
  };

  const clearCsv = () => {
    setCsvSymbols([]);
    setCsvFilename("");
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const importIntoList = async (listId, symbols) => {
    if (!listId || !symbols || symbols.length === 0) return null;
    setImportingId(listId);
    try {
      const r = await axios.post(
        `${API}/watchlists/${encodeURIComponent(listId)}/import`,
        { symbols },
      );
      return r.data;
    } catch (err) {
      setError(err?.response?.data?.detail || "Import failed");
      return null;
    } finally {
      setImportingId(null);
    }
  };

  const createList = async (e) => {
    e?.preventDefault?.();
    const name = (newName || "").trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    setImportSummary(null);
    try {
      const r = await axios.post(`${API}/watchlists`, { name, scope: newScope });
      const createdId = r.data?.id;
      setNewName("");
      let summary = null;
      // Iter121.4 — If a CSV was queued, bulk-import it right after
      // create so the operator sees the fresh list already populated.
      if (createdId && csvSymbols.length > 0) {
        summary = await importIntoList(createdId, csvSymbols);
        setImportSummary(summary);
      }
      await onRefresh();
      if (createdId) {
        await onSwitchActive(createdId);
        // Iter121.4.1 — Keep the modal open ONLY when we just imported
        // a CSV so the operator can see the summary chip; otherwise
        // auto-close per Iter121.3.1 behaviour.
        if (!summary) onClose?.();
      }
      clearCsv();
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  // Iter121.4 — Per-row "Import CSV" action. Wires a hidden file input
  // to whichever row was clicked; when the file lands it parses and
  // POSTs to /import for that specific list.
  const openRowCsvPicker = (listId) => {
    setRowImportTarget(listId);
    setImportSummary(null);
    setError(null);
    if (rowCsvInputRef.current) {
      rowCsvInputRef.current.value = "";
      rowCsvInputRef.current.click();
    }
  };

  const onRowCsvChange = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !rowImportTarget) return;
    try {
      const text = await file.text();
      const syms = parseCsvSymbols(text);
      if (syms.length === 0) {
        setError("No symbols found in the CSV.");
        return;
      }
      const summary = await importIntoList(rowImportTarget, syms);
      if (summary) {
        setImportSummary(summary);
        await onRefresh();
      }
    } catch (_e) {
      setError("Could not read the file");
    } finally {
      setRowImportTarget(null);
    }
  };

  // Iter121.5 — Export a watchlist as CSV. Fetches the current items
  // for the list, formats them as a header-plus-symbol CSV that our
  // own import parser can round-trip, then triggers a browser
  // download via an object URL. Empty lists still produce a valid
  // CSV (just a header) so the operator can distinguish "empty list"
  // from "download failed".
  const [exportingId, setExportingId] = useState(null);
  const exportList = async (w) => {
    if (!w?.id) return;
    setExportingId(w.id);
    setError(null);
    try {
      const r = await axios.get(`${API}/watchlist`, { params: { watchlist_id: w.id } });
      const items = Array.isArray(r.data) ? r.data : [];
      const escape = (s) => {
        const v = String(s ?? "");
        return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      };
      const header = "Symbol,Name,Added At";
      const rows = items.map(
        (it) => `${escape(it.symbol)},${escape(it.name || it.symbol)},${escape(it.added_at || "")}`,
      );
      const csv = [header, ...rows].join("\n") + "\n";
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Filename: sanitised list name + item count + date.
      const safe = (w.name || "watchlist").replace(/[^\w\-]+/g, "_").toLowerCase();
      const d = new Date();
      const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
      a.download = `${safe}_${items.length}_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Export failed");
    } finally {
      setExportingId(null);
    }
  };

  const beginRename = (w) => {
    setRenamingId(w.id);
    setRenameDraft(w.name || "");
    setError(null);
  };
  const commitRename = async (w) => {
    const name = (renameDraft || "").trim();
    if (!name || name === w.name) { setRenamingId(null); return; }
    try {
      await axios.put(`${API}/watchlists/${encodeURIComponent(w.id)}`, { name });
      setRenamingId(null);
      await onRefresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Rename failed");
    }
  };

  const removeList = async (w) => {
    if (w.is_default) { setError("Default lists cannot be deleted."); return; }
    setDeletingId(w.id);
    try {
      await axios.delete(`${API}/watchlists/${encodeURIComponent(w.id)}`);
      await onRefresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed");
    }
    setDeletingId(null);
  };

  const canMutate = (w) => (w.scope === "shared" ? isAdmin : true);

  return (
    <div
      data-testid={`${testidPrefix}-backdrop`}
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid={`${testidPrefix}-modal`}
        className="bg-[#0A0A0D] border border-[#26262B] rounded-sm shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#26262B] sticky top-0 bg-[#0A0A0D]">
          <div className="text-sm font-bold text-white tracking-tight">Manage Watchlists</div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testidPrefix}-close`}
            className="text-[#8A8A93] hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          <form onSubmit={createList} className="space-y-2" data-testid={`${testidPrefix}-create-form`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Create new list</div>
            <input
              type="text"
              data-testid={`${testidPrefix}-create-name`}
              placeholder="Name (e.g. Wheel Universe)"
              value={newName}
              maxLength={80}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] focus:outline-none text-white rounded-sm px-2 py-1.5 text-[12px] font-mono"
            />
            {isAdmin && (
              <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em]">
                <button
                  type="button"
                  onClick={() => setNewScope("personal")}
                  data-testid={`${testidPrefix}-scope-personal`}
                  className={`flex-1 py-1 rounded-sm border ${newScope === "personal" ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#26262B] text-[#8A8A93]"}`}
                >Personal</button>
                <button
                  type="button"
                  onClick={() => setNewScope("shared")}
                  data-testid={`${testidPrefix}-scope-shared`}
                  className={`flex-1 py-1 rounded-sm border ${newScope === "shared" ? "border-[#22D3FF] text-[#22D3FF] bg-[#22D3FF]/10" : "border-[#26262B] text-[#8A8A93]"}`}
                >Shared</button>
              </div>
            )}
            {/* Iter121.4 — Optional CSV import at create time. Client parses
                the file, backend bulk-imports after the list is created. */}
            <div className="flex items-center gap-2">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                onChange={(e) => handleCsvPick(e.target.files?.[0])}
                data-testid={`${testidPrefix}-csv-input`}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => csvInputRef.current?.click()}
                data-testid={`${testidPrefix}-csv-pick`}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#26262B] hover:border-[#22D3FF]/60 text-[10px] font-bold uppercase tracking-[0.12em] text-[#22D3FF]"
              >
                <Upload size={11} /> {csvSymbols.length > 0 ? `${csvSymbols.length} tickers` : "Upload CSV"}
              </button>
              {csvFilename && (
                <span className="flex-1 min-w-0 flex items-center gap-1 text-[10px] font-mono text-[#8A8A93] truncate" data-testid={`${testidPrefix}-csv-filename`}>
                  <FileText size={10} className="flex-shrink-0" />
                  <span className="truncate">{csvFilename}</span>
                  <button
                    type="button"
                    onClick={clearCsv}
                    data-testid={`${testidPrefix}-csv-clear`}
                    className="flex-shrink-0 text-[#FF3366]/70 hover:text-[#FF3366]"
                    title="Clear CSV"
                  ><X size={10} /></button>
                </span>
              )}
            </div>
            {csvSymbols.length > 0 && (
              <div className="text-[9px] font-mono text-[#5F5F66] truncate" data-testid={`${testidPrefix}-csv-preview`}>
                Preview: {csvSymbols.slice(0, 10).join(", ")}{csvSymbols.length > 10 ? ` … +${csvSymbols.length - 10} more` : ""}
              </div>
            )}
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              data-testid={`${testidPrefix}-create-submit`}
              className="w-full bg-[#00FF88] text-[#08080A] py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-[0.15em] disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {creating ? <Loader2 size={11} className="animate-spin" /> : <span className="text-sm leading-none">+</span>} {csvSymbols.length > 0 ? `Create + import ${csvSymbols.length}` : "Create list"}
            </button>
            {createError && <div className="text-[10px] font-mono text-[#FF3366]" data-testid={`${testidPrefix}-create-error`}>{createError}</div>}
          </form>

          {/* Iter121.4 — Import outcome chip. Renders after any bulk import
              (whether triggered by the create form or a row's Import button). */}
          {importSummary && (
            <div
              data-testid={`${testidPrefix}-import-summary`}
              className="text-[10px] font-mono px-2.5 py-1.5 rounded-sm border border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[#22D3FF] flex items-center gap-2 flex-wrap"
            >
              <span className="font-bold">Import:</span>
              <span>+{importSummary.counts?.added ?? 0} added</span>
              <span className="text-[#8A8A93]">·</span>
              <span>{importSummary.counts?.skipped ?? 0} skipped</span>
              {(importSummary.counts?.invalid ?? 0) > 0 && (<><span className="text-[#8A8A93]">·</span><span className="text-[#FFB800]">{importSummary.counts.invalid} invalid</span></>)}
              {(importSummary.counts?.truncated ?? 0) > 0 && (<><span className="text-[#8A8A93]">·</span><span className="text-[#FF3366]">{importSummary.counts.truncated} truncated</span></>)}
              <button
                type="button"
                onClick={() => setImportSummary(null)}
                data-testid={`${testidPrefix}-import-summary-close`}
                className="ml-auto text-[#22D3FF]/70 hover:text-[#22D3FF]"
              ><X size={10} /></button>
            </div>
          )}

          <div className="border-t border-[#26262B]" />

          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">Your lists</div>
          <div className="space-y-1" data-testid={`${testidPrefix}-list`}>
            {(watchlists || []).map((w) => (
              <div
                key={w.id}
                data-testid={`${testidPrefix}-row-${w.id}`}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border ${w.id === activeId ? "border-[#00FF88]/40 bg-[#00FF88]/5" : "border-[#1A1A20]"}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${w.scope === "shared" ? "bg-[#22D3FF]" : "bg-[#FFB800]"}`} />
                {renamingId === w.id ? (
                  <input
                    autoFocus
                    type="text"
                    value={renameDraft}
                    maxLength={80}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onBlur={() => commitRename(w)}
                    onKeyDown={(e) => { if (e.key === "Enter") commitRename(w); if (e.key === "Escape") setRenamingId(null); }}
                    data-testid={`${testidPrefix}-rename-input-${w.id}`}
                    className="flex-1 min-w-0 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] focus:outline-none text-white rounded-sm px-1.5 py-0.5 text-[11px] font-mono"
                  />
                ) : (
                  <span className="flex-1 min-w-0 truncate text-[11px] font-mono text-white">
                    {w.name}
                    {w.is_default && <span className="ml-1.5 text-[8px] uppercase tracking-[0.1em] text-[#5F5F66]">·default</span>}
                    <span className="ml-1.5 text-[9px] text-[#5F5F66]">({w.item_count ?? 0})</span>
                  </span>
                )}
                {renamingId !== w.id && (
                  <button
                    type="button"
                    onClick={() => exportList(w)}
                    disabled={exportingId === w.id}
                    data-testid={`${testidPrefix}-export-${w.id}`}
                    title="Export list as CSV"
                    className="text-[#FFB800]/70 hover:text-[#FFB800] disabled:opacity-50"
                  >
                    {exportingId === w.id ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                  </button>
                )}
                {canMutate(w) && renamingId !== w.id && (
                  <>
                    <button
                      type="button"
                      onClick={() => openRowCsvPicker(w.id)}
                      disabled={importingId === w.id}
                      data-testid={`${testidPrefix}-import-${w.id}`}
                      title="Import CSV"
                      className="text-[#22D3FF]/70 hover:text-[#22D3FF] disabled:opacity-50"
                    >
                      {importingId === w.id ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => beginRename(w)}
                      data-testid={`${testidPrefix}-rename-${w.id}`}
                      title="Rename"
                      className="text-[#8A8A93] hover:text-white text-[10px] uppercase tracking-[0.1em]"
                    >Rename</button>
                    {!w.is_default && (
                      <button
                        type="button"
                        onClick={() => removeList(w)}
                        disabled={deletingId === w.id}
                        data-testid={`${testidPrefix}-delete-${w.id}`}
                        title="Delete"
                        className="text-[#FF3366]/70 hover:text-[#FF3366] disabled:opacity-50"
                      >
                        {deletingId === w.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          {/* Iter121.4 — Hidden file input drives the per-row Import
              CSV button. `rowImportTarget` selects which list receives
              the upload; a single hidden input handles every row so we
              stay lean on DOM nodes. */}
          <input
            ref={rowCsvInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            onChange={onRowCsvChange}
            data-testid={`${testidPrefix}-row-csv-input`}
            className="hidden"
          />
          {error && <div className="text-[10px] font-mono text-[#FF3366]" data-testid={`${testidPrefix}-error`}>{error}</div>}
        </div>
      </div>
    </div>
  );
}
